import { useEffect, useState } from "react";
import {
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  useTheme,
} from "@mui/material";
import { FerramanetasDaListagem } from "../../shared/components/ferramentasDaListagem/ferramentas-listagem";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { ENVIRONMENT } from "../../environment/environment";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PaginacaoListagem } from "../../shared/components/ferramentasDaListagem/paginacao-listagem";
import { useDebounce } from "../../shared/utils/useDebounce/use-debounce";
import { toastError } from "../../shared/services/errors/erros-handler";
import { funcionarioService } from "./service/funcionario.service";
import { setores } from "./contracts/criar-funcionario.contract";
import { TabelaDaListagem } from "../../shared/components/tabela-da-listagem/tabelaDalistagem";
import { formatarCpfCnpj, toSlug } from "../../shared/utils";

export function ListagemFuncionarios() {
  const [funcionarios, setFuncionarios] = useState<any[]>([]);
  const [busca, setBusca] = useState("");
  const [page, setPage] = useState(0); // MUI começa em 0
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState<string | undefined>(undefined);

  const buscaDebounce = useDebounce(query || "", 500);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const reload = location.state?.reload;

  useEffect(() => {
    const normalizarDocumento = (valor: string) => valor.replace(/[^\d]/g, "");

    const gerarQuery = () => {
      if (!busca || busca.trim() === "") return undefined;

      const valorLimpo = normalizarDocumento(busca);
      const isNumerico = /^\d+$/.test(valorLimpo);

      if (isNumerico) {
        if (valorLimpo.length === 11) {
          return `campo_pesquisa=cpf&tipo_pesquisa=in&valor_pesquisa=${valorLimpo}`;
        } else if (valorLimpo.length >= 14) {
          return `campo_pesquisa=cnpj&tipo_pesquisa=in&valor_pesquisa=${valorLimpo}`;
        }
      }

      return `campo_pesquisa=slug&tipo_pesquisa=in&valor_pesquisa=${toSlug(
        busca
      )}`;
    };

    setQuery(gerarQuery());
  }, [busca]);

  const carregarFuncionarios = async (
    query?: string,
    pagina = page,
    limite = rowsPerPage
  ) => {
    try {
      const result = await funcionarioService.findAll(query, pagina, limite);
      setFuncionarios(result.items || []);
      setTotalCount(result?.meta?.totalItems || 0);
      setIsLoading(false);
    } catch (error) {
      console.error("Erro ao buscar funcionarios:", error);
      setFuncionarios([]);
    }
  };

  useEffect(() => {
    if (reload) {
      setIsLoading(true);
      carregarFuncionarios(undefined, 0);
    }
  }, [reload]);

  useEffect(() => {
    setPage(0);
    carregarFuncionarios(buscaDebounce, 0);
  }, [buscaDebounce]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    carregarFuncionarios(buscaDebounce, newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    carregarFuncionarios(buscaDebounce, 0, newLimit);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(`Tem certeza que deseja excluir o colaborador?`)) {
      try {
        await funcionarioService.deletar(id);
        toast.success(`colaborador excluído com sucesso!`);
        carregarFuncionarios(buscaDebounce, page, rowsPerPage);
      } catch (error) {
        toastError(error);
      }
    }
  };

  return (
    <BaseLayout
      title="Colaboradores"
      barraDeFerramentas={
        <FerramanetasDaListagem
          placeholder={ENVIRONMENT.INPUT_DE_BUSCA + " por nome, cpf e cnpj"}
          textoBotaoNovo="Adicionar Novo"
          mostrarInputBusca
          textoDaBusca={busca}
          aoMudarTextoDeBusca={setBusca}
          aoClicarBotaoNovo={() => navigate("/colaborador/criar")}
        />
      }
    >
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          border: "1px solid #ccc",
          borderRadius: 2,
        }}
      >
        <TabelaDaListagem
          ariaLabel="tabela de funcionarios"
          headTable={[
            "Nome",
            "Obra",
            "CPF/CNPJ",
            "Cargo",
            "Status",
            "Responsável pela Obra",
            "Ações",
          ]}
          tableBody={
            funcionarios.length > 0 ? (
              funcionarios.map((funcionario) => (
                <TableRow hover key={funcionario.id}>
                  <TableCell>
                    {funcionario.fullName || funcionario.corporateName}
                  </TableCell>
                  <TableCell>{funcionario.work?.workName ?? "—"}</TableCell>
                  <TableCell>{formatarCpfCnpj(funcionario?.cpf) || formatarCpfCnpj(funcionario?.cnpj)}</TableCell>
                  <TableCell>
                    {funcionario.position?.name || setores[funcionario.sector]}
                  </TableCell>
                  <TableCell>
                    {funcionario.active ? "Ativado" : "Desativado"}
                  </TableCell>
                  <TableCell>
                    {funcionario.isResponsible ? "Sim" : "Não"}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() =>
                        navigate(`/colaborador/detalhe/${funcionario.id}`)
                      }
                    >
                      <Icon>edit</Icon>
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(funcionario.id)}
                    >
                      <Icon>delete</Icon>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  
                </TableCell>
              </TableRow>
            )
          }
        />
        <PaginacaoListagem
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </BaseLayout>
  );
}
